import type {
  MonitorConclusion,
  MonitorState,
  PerfBand,
  PerfMetricKey,
} from "./types";

/** "just now", "12 min ago", "5 h ago", "3 d ago". */
export const formatAge = (hours: number): string => {
  if (!Number.isFinite(hours) || hours < 0) return "unknown";
  const minutes = Math.round(hours * 60);
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 48) return `${Math.round(hours)} h ago`;
  return `${Math.round(hours / 24)} d ago`;
};

/** From the longest usual gap between runs: "runs at least every ~30 min",
 *  "~18 h", "~7 d"; null when unknown. "At least" because the gap is the
 *  long one: scripted rides alternate 6 h and 18 h gaps. */
export const formatCadence = (hours: number | null): string | null => {
  if (hours === null || !Number.isFinite(hours) || hours <= 0) return null;
  const every = "runs at least every";
  if (hours < 1) return `${every} ~${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 48) return `${every} ~${Math.round(hours)} h`;
  return `${every} ~${Math.round(hours / 24)} d`;
};

/** Money in its own currency, e.g. "$2,997.00", "€890.10". Falls back to
 *  "123.45 XYZ" for a code Intl does not know. */
export const formatMoney = (currency: string, amount: string): string => {
  const value = Number(amount);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
};

/** "+29%" / "-12%" / "0%"; null passes through as null. */
export const formatChange = (percent: number | null): string | null => {
  if (percent === null) return null;
  if (percent > 0) return `+${percent}%`;
  return `${percent}%`;
};

/** Today's weekday name in the report's timezone, for "last 4 Tuesdays". */
export const weekdayIn = (timezone: string, at: Date): string =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    timeZone: timezone,
  }).format(at);

export const STATE_LABEL: Record<MonitorState, string> = {
  failing: "Failing",
  stale: "Stale",
  unknown: "New",
  ok: "OK",
};

export const STATE_STYLE: Record<MonitorState, string> = {
  failing: "bg-red-100 text-red-800",
  stale: "bg-amber-100 text-amber-800",
  unknown: "bg-gray-100 text-gray-700",
  ok: "bg-green-100 text-green-800",
};

export const CONCLUSION_COLOR: Record<MonitorConclusion, string> = {
  success: "bg-green-500",
  failure: "bg-red-500",
  timed_out: "bg-red-500",
  cancelled: "bg-gray-400",
  skipped: "bg-gray-300",
};

/** Turn a monitor slug into a readable name: "tracking-battery" to
 *  "Tracking battery". */
export const monitorTitle = (slug: string): string => {
  const words = slug.replace(/-/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : slug;
};

/** Bytes as the perf probe reports them: "981 KB", "1.6 MB". KB below a
 *  megabyte (1000-based, like the probe's own budgets). */
export const formatBytes = (bytes: number | null): string => {
  if (bytes === null || !Number.isFinite(bytes)) return "n/a";
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
};

/** Lab timing: "4.2 s", or "850 ms" under a second. */
export const formatMs = (ms: number | null): string => {
  if (ms === null || !Number.isFinite(ms)) return "n/a";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

/** A lab metric the way Lighthouse prints it: seconds for the timings
 *  ("4.3 s", "850 ms"), two decimals for CLS ("0.05"). */
export const formatMetric = (
  metric: PerfMetricKey,
  value: number | null,
): string => {
  if (value === null || !Number.isFinite(value)) return "n/a";
  if (metric === "cls") return value.toFixed(2);
  return formatMs(value);
};

export const METRIC_SHORT: Record<PerfMetricKey, string> = {
  lcp: "LCP",
  cls: "CLS",
  tbt: "TBT",
  fcp: "FCP",
  ttfb: "TTFB",
};

export const BAND_LABEL: Record<PerfBand, string> = {
  good: "Good",
  "needs-improvement": "Needs improvement",
  poor: "Poor",
};

/** Lighthouse's colours: green, orange, red. Unmeasured is gray. */
export const BAND_STYLE: Record<PerfBand | "none", string> = {
  good: "bg-green-100 text-green-800",
  "needs-improvement": "bg-amber-100 text-amber-800",
  poor: "bg-red-100 text-red-800",
  none: "bg-gray-100 text-gray-500",
};

export const BAND_TEXT: Record<PerfBand | "none", string> = {
  good: "text-green-700",
  "needs-improvement": "text-amber-700",
  poor: "text-red-700",
  none: "text-gray-500",
};

/** PageSpeed Insights for a page: Lighthouse plus CrUX field data, the
 *  cheap way to put real-user numbers next to the lab ones. */
export const psiUrl = (pageUrl: string): string =>
  `https://pagespeed.web.dev/analysis?url=${encodeURIComponent(pageUrl)}&form_factor=mobile`;

/** Percent change from `from` to `to`, rounded; null when either is
 *  missing or `from` is 0. */
export const percentChange = (
  from: number | null | undefined,
  to: number | null | undefined,
): number | null => {
  if (from == null || to == null || from === 0) return null;
  return Math.round(((to - from) / from) * 100);
};

// The dollar wording is shared with the Checkout page (OMG-2793).
export { formatUsd, mispricingText } from "@lib/usd";
